import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, getLegacyBanReview, liftLegacyBans, type LegacyBanReview as Review } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { AccountTarget } from "./AccountActionDialog";
import { captureLegacyBanCorrection, createSubmitLock } from "./action-model";

function dateLabel(value: string | number | null, empty: string) {
  if (value == null) return empty;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

export function LegacyBanReview({ target, disabled, onState }: {
  target: AccountTarget; disabled: boolean; onState: (pending: boolean, changed: boolean) => void;
}) {
  const [review, setReview] = useState<Review | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const lock = useRef(createSubmitLock());
  const qc = useQueryClient();
  const backendOnly = target.id.toUpperCase().startsWith("EC2X");

  useEffect(() => { onState(pending, selected.length > 0 || reason.trim().length > 0); }, [pending, selected, reason, onState]);

  const read = useCallback(async (source: Review["source"]) => {
    if (!lock.current.begin()) return;
    setPending(true); setError(""); setSelected([]); setNeedsReview(true);
    try {
      const next = await getLegacyBanReview(target.id, source);
      if (next.playfabId !== target.id || next.source !== source) throw new Error("The returned review does not match this account and source.");
      setReview(next); setNeedsReview(false);
    } catch (e) { setError(`Could not refresh legacy bans: ${String(e)}. Refresh the review before taking action.`); }
    finally { setPending(false); lock.current.end(); }
  }, [target.id]);

  useEffect(() => { if (!backendOnly) void read("archive"); }, [backendOnly, read]);

  async function submit() {
    if (!review || needsReview || disabled) return;
    const action = captureLegacyBanCorrection(target, review, selected, reason, crypto.randomUUID());
    if (!action || !lock.current.begin()) return;
    setPending(true); setError(""); setMessage(""); setNeedsReview(true);
    try {
      const result = await liftLegacyBans(action.id, action.correction);
      if (result.confirmed !== true || result.playfabId !== action.id || result.sourceTitle !== action.correction.sourceTitle
        || result.source !== action.correction.source || !Array.isArray(result.banIds)
        || result.banIds.length !== action.correction.banIds.length || new Set(result.banIds).size !== result.banIds.length
        || action.correction.banIds.some(id => !result.banIds.includes(id))) throw new Error("The correction was not confirmed for all selected bans.");
      setMessage("Selected legacy ban corrections were confirmed. Refresh the review to see the current status. Other backend bans remain in effect.");
      setSelected([]); setReason("");
      for (const key of ["player-bans", "audit", "recent-accounts", "flags"]) void qc.invalidateQueries({ queryKey: [key] });
    } catch (e) {
      setError(e instanceof ApiError && (e.status === 409 || e.status === 502)
        ? "The correction is not confirmed. Refresh this review and inspect the selected bans before trying again."
        : `The correction is not confirmed: ${String(e)}. Refresh the review before trying again.`);
    } finally { setPending(false); lock.current.end(); }
  }

  return <section className="space-y-3 border-t border-border pt-4" aria-label="Legacy ban review">
    <h3 className="font-medium">Legacy ban review</h3>
    <p className="text-xs text-muted-foreground">Review individual legacy bans for this exact player ID. Lifting a legacy ban does not clear unrelated backend bans.</p>
    {backendOnly ? <p className="text-sm text-muted-foreground">This backend-created account has no legacy PlayFab account.</p> : <>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={pending || disabled} onClick={() => { setMessage(""); void read(review?.source ?? "archive"); }}>Refresh review</Button>
        {review?.liveReviewEnabled && <Button variant="secondary" size="sm" disabled={pending || disabled} onClick={() => { setMessage(""); void read("playfab"); }}>Read live PlayFab bans</Button>}
        {review?.source === "playfab" && <Button variant="outline" size="sm" disabled={pending || disabled} onClick={() => { setMessage(""); void read("archive"); }}>Read archived bans</Button>}
      </div>
      {pending && <p role="status" className="text-sm">Working…</p>}
      {review && <>
        <div className="rounded border border-border p-2 text-xs space-y-1">
          <p>Player ID: <span className="font-mono">{review.playfabId}</span></p>
          <p>Title: <span className="font-mono">{review.sourceTitle}</span> · Source: {review.source === "playfab" ? "Live PlayFab" : "Archived PlayFab records"}</p>
        </div>
        {!review.archiveRecorded && review.source === "archive" && <p className="text-sm text-muted-foreground">No archived ban evidence is recorded for this account.</p>}
        {review.bans.length === 0 && <p className="text-sm text-muted-foreground">No legacy bans found in this source.</p>}
        <fieldset disabled={pending || disabled || needsReview} className="space-y-3">
          {review.bans.map(ban => <label key={ban.banId} className="flex gap-2 rounded border border-border p-2 text-sm">
            <input type="checkbox" checked={selected.includes(ban.banId)} disabled={ban.corrected || ban.sharedScope}
              onChange={e => setSelected(current => e.target.checked ? [...current, ban.banId] : current.filter(id => id !== ban.banId))} />
            <span className="space-y-1"><span className="block font-mono text-xs">{ban.banId}</span>
              <span className="block">{ban.reason || "No reason recorded"}</span>
              <span className="block text-xs text-muted-foreground">{ban.corrected ? "Correction recorded" : ban.active ? "Active" : "Inactive"} · Created: {dateLabel(ban.createdAt, "Unknown")} · Expires: {dateLabel(ban.expiresAt, "Permanent")}</span>
              {!ban.active && !ban.corrected && !ban.sharedScope && <span className="block text-xs text-muted-foreground">Record a reviewed lift to preserve this correction during archive import.</span>}
              {ban.sharedScope && <span className="block text-xs text-destructive">Shared IP or family scope: requires a separate review.</span>}
            </span>
          </label>)}
          <label className="block space-y-1 text-sm">Review reason / case reference (required)
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why these selected bans were incorrect" />
          </label>
          <Button variant="destructive" onClick={() => void submit()} disabled={!reason.trim() || selected.length === 0}>Confirm selected legacy ban corrections</Button>
        </fieldset>
        {needsReview && <p className="text-sm text-muted-foreground">Refresh this review before taking further action.</p>}
      </>}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {message && <Alert><AlertDescription>{message}</AlertDescription></Alert>}
    </>}
  </section>;
}
